import { notifications } from '@mantine/notifications';
import {
  createReference,
  MedplumClient,
  normalizeErrorString,
} from '@medplum/core';
import {
  Bundle,
  BundleEntry,
  Encounter,
  Patient,
  Questionnaire,
  QuestionnaireResponse,
} from '@medplum/fhirtypes';
import { useCallback } from 'react';
import { IconCircleCheck, IconCircleOff } from '@tabler/icons-react';
import { pruneDisabledAnswers } from '@/utils/prune-questionnaire-answers';
import { useTranslation } from 'react-i18next';

interface UseQuestionnaireSubmitHandlerParams {
  medplum: MedplumClient;
  patient: Patient | undefined;
  encounter: Encounter | undefined;
  questionnaire: Questionnaire | undefined;
  questionnaireResponse: QuestionnaireResponse | undefined;
  status: 'completed' | 'in-progress';
}

export function useQuestionnaireSubmitHandler({
  medplum,
  patient,
  encounter,
  questionnaire,
  questionnaireResponse,
  status,
}: UseQuestionnaireSubmitHandlerParams) {
  const { t } = useTranslation(['patient-pages', 'common']);

  return useCallback(
    async (newResponse: QuestionnaireResponse) => {
      try {
        const practitioner = medplum.getProfile();

        if (!encounter) {
          throw new Error('You must have an active encounter!');
        }

        if (!practitioner || !patient || !questionnaire) {
          throw new Error('Invalid state');
        }

        // Clean up disabled answers before persisting
        const prunedResponse: QuestionnaireResponse = {
          ...newResponse,
          item: pruneDisabledAnswers(
            questionnaire.item,
            newResponse.item,
            newResponse
          ),
        };

        // Build bundle entries
        const entries: BundleEntry[] = [];

        // Questionnaire response entry
        if (questionnaireResponse?.id) {
          entries.push({
            resource: {
              ...questionnaireResponse,
              ...prunedResponse,
              status: status,
              subject: createReference(patient),
            },
            request: {
              method: 'PUT',
              url: `QuestionnaireResponse/${questionnaireResponse.id}`,
            },
          });
        } else {
          entries.push({
            resource: {
              ...prunedResponse,
              resourceType: 'QuestionnaireResponse',
              questionnaire: `Questionnaire/${questionnaire.id}`,
              source: createReference(practitioner),
              authored: new Date().toISOString(),
              status: status,
              subject: createReference(patient),
              encounter: createReference(encounter),
            },
            request: {
              method: 'POST',
              url: 'QuestionnaireResponse',
            },
          });
        }

        // Execute as a single transaction bundle if we have additional resources,
        // otherwise use the simple update/create
        if (entries.length > 1) {
          const bundle: Bundle = {
            resourceType: 'Bundle',
            type: 'transaction',
            entry: entries,
          };
          await medplum.executeBatch(bundle);
        } else if (questionnaireResponse?.id) {
          // Single resource update
          await medplum.updateResource<QuestionnaireResponse>({
            ...questionnaireResponse,
            ...prunedResponse,
            status: status,
            subject: createReference(patient),
          });
        } else {
          // Single resource create
          await medplum.createResource({
            questionnaire: `Questionnaire/${questionnaire.id}`,
            source: createReference(practitioner),
            authored: new Date().toISOString(),
            ...prunedResponse,
            status: status,
            subject: createReference(patient),
            encounter: createReference(encounter),
          });
        }

        notifications.show({
          color: 'green',
          icon: <IconCircleCheck />,
          title: t('success', { ns: 'common' }),
          message: `${t('medicalHistory.notifications.submitMessage', {
            ns: 'patient-pages',
          })}.`,
        });
      } catch (error: any) {
        notifications.show({
          color: 'red',
          icon: <IconCircleOff />,
          title: t('error', { ns: 'common' }),
          message: normalizeErrorString(error),
        });
      }
    },
    [
      medplum,
      patient,
      encounter,
      questionnaire,
      questionnaireResponse,
      status,
      t,
    ]
  );
}
